import { forwardRef, useState, useEffect, type InputHTMLAttributes } from 'react';

export interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Se o campo tem erro de validacao */
  hasError?: boolean;
  /** Mensagem de erro */
  errorMessage?: string;
  /** Label do campo */
  label?: string;
  /** Se o campo é obrigatório */
  required?: boolean;
  /** Classes CSS adicionais para o container */
  containerClassName?: string;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      hasError = false,
      errorMessage,
      label,
      required = false,
      containerClassName = '',
      className = '',
      ...props
    },
    ref
  ) => {
    const [shouldShake, setShouldShake] = useState(false);

    // Trigger shake animation when error appears
    useEffect(() => {
      if (hasError) {
        setShouldShake(true);
        const timer = setTimeout(() => setShouldShake(false), 500);
        return () => clearTimeout(timer);
      }
    }, [hasError]);

    const inputClasses = [
      'w-full rounded-2xl border bg-white/80 px-4 py-3 text-sm text-slate-700 outline-none transition focus:ring-2',
      hasError
        ? 'border-red-300 focus:border-red-300 focus:ring-red-100'
        : 'border-slate-200 focus:border-success-200 focus:ring-success-100',
      shouldShake ? 'animate-shake' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={['space-y-2', containerClassName].filter(Boolean).join(' ')}>
        {label && (
          <label className="block text-sm font-semibold text-slate-700">
            <span className="text-xs uppercase tracking-[0.3em] text-slate-400">
              {label}
              {required && ' *'}
            </span>
          </label>
        )}
        <input ref={ref} className={inputClasses} {...props} />
        {hasError && errorMessage && (
          <p className="text-xs text-red-600">{errorMessage}</p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export interface FormTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Se o campo tem erro de validacao */
  hasError?: boolean;
  /** Mensagem de erro */
  errorMessage?: string;
  /** Label do campo */
  label?: string;
  /** Se o campo é obrigatório */
  required?: boolean;
  /** Classes CSS adicionais para o container */
  containerClassName?: string;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      hasError = false,
      errorMessage,
      label,
      required = false,
      containerClassName = '',
      className = '',
      ...props
    },
    ref
  ) => {
    const [shouldShake, setShouldShake] = useState(false);

    // Trigger shake animation when error appears
    useEffect(() => {
      if (hasError) {
        setShouldShake(true);
        const timer = setTimeout(() => setShouldShake(false), 500);
        return () => clearTimeout(timer);
      }
    }, [hasError]);

    const textareaClasses = [
      'w-full rounded-2xl border bg-white/80 px-4 py-3 text-sm text-slate-700 outline-none transition focus:ring-2 resize-none',
      hasError
        ? 'border-red-300 focus:border-red-300 focus:ring-red-100'
        : 'border-slate-200 focus:border-success-200 focus:ring-success-100',
      shouldShake ? 'animate-shake' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={['space-y-2', containerClassName].filter(Boolean).join(' ')}>
        {label && (
          <label className="block text-sm font-semibold text-slate-700">
            <span className="text-xs uppercase tracking-[0.3em] text-slate-400">
              {label}
              {required && ' *'}
            </span>
          </label>
        )}
        <textarea ref={ref} className={textareaClasses} {...props} />
        {hasError && errorMessage && (
          <p className="text-xs text-red-600">{errorMessage}</p>
        )}
      </div>
    );
  }
);

FormTextarea.displayName = 'FormTextarea';

/**
 * @deprecated Use `LecartteSelect` from `@lecartte/shared-ui/components/fields/LecartteSelect`.
 * Native select kept so screens that are not migrated yet keep working.
 */
export interface FormSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Se o campo tem erro de validacao */
  hasError?: boolean;
  /** Mensagem de erro */
  errorMessage?: string;
  /** Label do campo */
  label?: string;
  /** Se o campo é obrigatório */
  required?: boolean;
  /** Classes CSS adicionais para o container */
  containerClassName?: string;
}

/** @deprecated Use `LecartteSelect`. Native select kept during the migration. */
export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      hasError = false,
      errorMessage,
      label,
      required = false,
      containerClassName = '',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const [shouldShake, setShouldShake] = useState(false);

    // Trigger shake animation when error appears
    useEffect(() => {
      if (hasError) {
        setShouldShake(true);
        const timer = setTimeout(() => setShouldShake(false), 500);
        return () => clearTimeout(timer);
      }
    }, [hasError]);

    const selectClasses = [
      'w-full rounded-2xl border bg-white/80 px-4 py-3 text-sm text-slate-700 outline-none transition focus:ring-2',
      hasError
        ? 'border-red-300 focus:border-red-300 focus:ring-red-100'
        : 'border-slate-200 focus:border-success-200 focus:ring-success-100',
      shouldShake ? 'animate-shake' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={['space-y-2', containerClassName].filter(Boolean).join(' ')}>
        {label && (
          <label className="block text-sm font-semibold text-slate-700">
            <span className="text-xs uppercase tracking-[0.3em] text-slate-400">
              {label}
              {required && ' *'}
            </span>
          </label>
        )}
        <select ref={ref} className={selectClasses} {...props}>
          {children}
        </select>
        {hasError && errorMessage && (
          <p className="text-xs text-red-600">{errorMessage}</p>
        )}
      </div>
    );
  }
);

FormSelect.displayName = 'FormSelect';
